/**
 * BhaSchematic — a hand-drawn, to-scale SVG of a BHA run: the component stack
 * top-of-assembly → bit, with a realistic PDC bit at the bottom, a cumulative
 * length scale down the left, and leader-lined spec callouts on the right.
 *
 * Geometry is derived from the run's `assembly` (bottom-up, position 1 = bit):
 * we reverse it to draw top→bottom, lay each tool out proportional to its
 * length, widen it proportional to OD, and add blades to stabilizers and a bent
 * body to the motor/RSS. The PDC bit is drawn separately below the deepest tool.
 * Nothing here is computed for the record — it is a faithful drawing of served
 * numbers only.
 */
import { useMemo } from "react";
import type { BhaComponentOut } from "@api/types";
import { inchFrac } from "./inchFrac";

interface Props {
  /** Bottom-up assembly (position 1 = bit). */
  assembly: BhaComponentOut[];
  /** Vendor code → display label (from the Oxbo-M vendors catalog). */
  vendorLabel: (code: string | null) => string;
  /** Component-type code → display label (component_types catalog). */
  typeLabel: (code: string) => string;
  /** Bit MD (ft/m in stored units) for the bottom depth marker. */
  bitMd: number | null;
  /** Formatter for a depth value with its unit label. */
  fmtDepth: (v: number | null) => string;
  ariaLabel?: string;
}

const VIEW_W = 1180;
const TOP = 24;
const BODY_X = 400; // centerline
const SCALE_X = 92;
const STACK_TOP = 46;
const MIN_TOOL_PX = 30;
const CALLOUT_X = 660;
const CALLOUT_W = 500;

type Kind = "bit" | "stab" | "motor" | "mwd" | "plain";

function kindOf(code: string): Kind {
  const c = code.toUpperCase();
  if (c === "BIT") return "bit";
  if (c === "STAB" || c === "NBSTAB") return "stab";
  if (c === "MOTOR" || c === "RSS") return "motor";
  if (c === "MWD" || c === "LWD") return "mwd";
  return "plain";
}

export function BhaSchematic({
  assembly,
  vendorLabel,
  typeLabel,
  bitMd,
  fmtDepth,
  ariaLabel,
}: Props) {
  // Draw top of assembly at the top; position 1 (the bit) is drawn last/lowest.
  const topDown = useMemo(() => [...assembly].sort((a, b) => b.position - a.position), [assembly]);
  const bit = assembly.find((c) => kindOf(c.component_type_entry_key) === "bit") ?? null;
  const tools = topDown.filter((c) => kindOf(c.component_type_entry_key) !== "bit");

  const totalLen = tools.reduce((s, c) => s + (c.length ?? 0), 0) || 1;
  const maxOd = Math.max(1, ...tools.map((c) => c.od_in ?? 0), bit?.od_in ?? 0);

  // Layout: proportional height per tool, floored so tiny subs stay visible.
  const AVAIL = 620;
  const laid = useMemo(() => {
    let cum = 0;
    let y = STACK_TOP;
    return tools.map((c) => {
      const len = c.length ?? 0;
      const h = Math.max(MIN_TOOL_PX, (len / totalLen) * AVAIL);
      const od = c.od_in ?? maxOd * 0.7;
      const halfW = 18 + (od / maxOd) * 62; // 18..80 px half-width
      const item = { c, y, h, halfW, cumTop: cum };
      cum += len;
      y += h + 12; // 12px joint gap
      return item;
    });
  }, [tools, totalLen, maxOd]);

  const last = laid[laid.length - 1];
  const stackBottom = last ? last.y + last.h : STACK_TOP;
  const bitTop = stackBottom + 14;
  const bitBottom = bitTop + 92;
  const viewH = bitBottom + 60;

  return (
    <div className="bha-schematic-wrap">
      <svg
        className="bha-svg"
        viewBox={`0 0 ${VIEW_W} ${viewH}`}
        preserveAspectRatio="xMidYMin meet"
        role="img"
        aria-label={
          ariaLabel ??
          "BHA component stack drawn to scale, top of assembly at top and bit at bottom"
        }
      >
        {/* centerline */}
        <line className="centerline" x1={BODY_X} y1={TOP} x2={BODY_X} y2={bitTop} />

        {/* cumulative-length scale */}
        <line className="scale-axis" x1={SCALE_X} y1={TOP} x2={SCALE_X} y2={stackBottom + 6} />
        <text x={12} y={16} fontSize={13} fill="var(--head)">
          Length (ft)
        </text>
        {laid.map(({ y, cumTop }) => (
          <g key={`sc-${y}`}>
            <line className="scale-axis" x1={SCALE_X - 12} y1={y} x2={SCALE_X} y2={y} />
            <text className="scale-txt" x={SCALE_X - 16} y={y + 4} textAnchor="end" fontSize={13}>
              {Math.round(cumTop)}
            </text>
          </g>
        ))}

        {/* top crossover to drill pipe */}
        <path
          className="joint"
          d={`M${BODY_X - 11} ${TOP} L${BODY_X + 11} ${TOP} L${BODY_X + 3} ${TOP + 20} L${BODY_X - 3} ${TOP + 20} Z`}
        />
        <text className="callout-spec" x={BODY_X + 28} y={TOP + 12}>
          to drill pipe
        </text>

        {/* the tools */}
        {laid.map(({ c, y, h, halfW }, i) => {
          const kind = kindOf(c.component_type_entry_key);
          const x = BODY_X - halfW;
          const w = halfW * 2;
          const cy = y + h / 2;
          const isMwd = kind === "mwd";
          const isMotor = kind === "motor";
          const isStab = kind === "stab";
          return (
            <g key={c.id}>
              {isMotor ? (
                <g transform={`rotate(2.2 ${BODY_X} ${cy})`}>
                  <rect className="tool-body" x={x} y={y} width={w} height={h} />
                  <line className="scale-axis" x1={x} y1={y + h * 0.32} x2={x + w} y2={y + h * 0.32} />
                  <line className="scale-axis" x1={x} y1={y + h * 0.68} x2={x + w} y2={y + h * 0.68} />
                </g>
              ) : (
                <rect className="tool-body" x={x} y={y} width={w} height={h} />
              )}
              {isStab ? (
                <>
                  <path
                    className="tool-blade"
                    d={`M${x} ${y + 3} L${x - 26} ${y + h * 0.4} L${x - 26} ${y + h * 0.6} L${x} ${y + h - 3} Z`}
                  />
                  <path
                    className="tool-blade"
                    d={`M${x + w} ${y + 3} L${x + w + 26} ${y + h * 0.4} L${x + w + 26} ${y + h * 0.6} L${x + w} ${y + h - 3} Z`}
                  />
                </>
              ) : null}
              {isMwd ? <circle cx={BODY_X} cy={cy} r={6} fill="var(--oxbo-z)" /> : null}
              {/* joint below */}
              <rect className="joint" x={BODY_X - 29} y={y + h} width={58} height={12} />

              {/* leader-lined callout */}
              <Callout
                y={STACK_TOP + i * ((viewH - STACK_TOP) / Math.max(laid.length, 1))}
                fromX={x + w}
                fromY={cy}
                kind={kind}
                name={typeLabel(c.component_type_entry_key)}
                spec={`OD ${inchFrac(c.od_in)} · ${c.length ?? "—"} ft${
                  c.vendor_entry_key ? ` · ${vendorLabel(c.vendor_entry_key)}` : ""
                }`}
              />
            </g>
          );
        })}

        {/* ── PDC bit ── */}
        {bit ? (
          <PdcBit
            cx={BODY_X}
            top={bitTop}
            odLabel={inchFrac(bit.od_in)}
            vendor={bit.vendor_entry_key ? vendorLabel(bit.vendor_entry_key) : null}
          />
        ) : null}

        {/* bottom-of-bit depth marker */}
        <line
          x1={SCALE_X}
          y1={bitBottom + 8}
          x2={BODY_X - 92}
          y2={bitBottom + 8}
          stroke="var(--ink)"
          strokeDasharray="2 4"
        />
        <text className="scale-txt" x={SCALE_X} y={bitBottom + 26} textAnchor="start" fontSize={14}>
          {fmtDepth(bitMd)} (bit)
        </text>
      </svg>
    </div>
  );
}

function Callout({
  y,
  fromX,
  fromY,
  kind,
  name,
  spec,
}: {
  y: number;
  fromX: number;
  fromY: number;
  kind: Kind;
  name: string;
  spec: string;
}) {
  const boxY = Math.max(TOP, Math.min(y, 900));
  const cls = kind === "mwd" ? "mwd" : kind === "bit" ? "bit" : "";
  return (
    <g>
      <line className="callout-line" x1={fromX} y1={fromY} x2={CALLOUT_X} y2={boxY + 18} />
      <rect
        className={`callout ${cls}`}
        x={CALLOUT_X}
        y={boxY}
        width={CALLOUT_W}
        height={44}
        rx={8}
      />
      <text className={`callout-name ${cls}`} x={CALLOUT_X + 16} y={boxY + 20}>
        {name}
      </text>
      <text className="callout-spec" x={CALLOUT_X + 16} y={boxY + 37}>
        {spec}
      </text>
    </g>
  );
}

/** A realistic 5-blade PDC bit: matrix taper, gauge pads, fanned blades with
 * PDC cutters, and three nozzles. Drawn centered on `cx`, top at `top`. */
function PdcBit({
  cx,
  top,
  odLabel,
  vendor,
}: {
  cx: number;
  top: number;
  odLabel: string;
  vendor: string | null;
}) {
  const bodyTop = top + 18;
  const faceMid = bodyTop + 26;
  const faceBottom = bodyTop + 68;
  return (
    <g>
      {/* pin into bit */}
      <rect className="joint" x={cx - 24} y={top} width={48} height={18} />
      {/* bit body / matrix taper */}
      <path
        className="tool-body"
        d={`M${cx - 91} ${bodyTop} L${cx + 91} ${bodyTop} L${cx + 67} ${faceBottom} Q${cx} ${faceBottom + 18} ${cx - 67} ${faceBottom} Z`}
      />
      {/* gauge pads */}
      <rect
        x={cx - 85}
        y={bodyTop}
        width={18}
        height={46}
        rx={3}
        fill="var(--surface-2)"
        stroke="var(--ink)"
        strokeWidth={1}
      />
      <rect
        x={cx + 67}
        y={bodyTop}
        width={18}
        height={46}
        rx={3}
        fill="var(--surface-2)"
        stroke="var(--ink)"
        strokeWidth={1}
      />
      {/* 5 blades fanning down the face */}
      {[
        [cx, bodyTop, cx, faceBottom + 10],
        [cx - 33, bodyTop + 4, cx - 60, faceBottom - 2],
        [cx + 33, bodyTop + 4, cx + 60, faceBottom - 2],
        [cx - 60, bodyTop + 11, cx - 33, faceBottom + 5],
        [cx + 60, bodyTop + 11, cx + 33, faceBottom + 5],
      ].map(([x1, y1, x2, y2], i) => (
        <path
          key={`bl-${i}`}
          d={`M${x1} ${y1} L${x2} ${y2}`}
          stroke="var(--ink)"
          strokeWidth={1.6}
          fill="none"
        />
      ))}
      {/* PDC cutters */}
      {[
        [cx, faceMid - 8, 4.2],
        [cx, faceMid + 12, 3.8],
        [cx, faceBottom, 3.4],
        [cx - 26, faceMid - 8, 4],
        [cx - 40, faceMid + 12, 3.7],
        [cx - 51, faceBottom - 3, 3.2],
        [cx + 26, faceMid - 8, 4],
        [cx + 40, faceMid + 12, 3.7],
        [cx + 51, faceBottom - 3, 3.2],
      ].map(([x, y, r], i) => (
        <circle key={`ct-${i}`} className="bit-cutter" cx={x} cy={y} r={r} />
      ))}
      {/* nozzles */}
      {[
        [cx - 22, faceMid + 6],
        [cx + 22, faceMid + 6],
        [cx, faceMid + 22],
      ].map(([x, y], i) => (
        <circle
          key={`nz-${i}`}
          cx={x}
          cy={y}
          r={2.4}
          fill="var(--surface)"
          stroke="var(--ink)"
          strokeWidth={1}
        />
      ))}
      {/* callout */}
      <line
        className="callout-line"
        x1={cx + 91}
        y1={faceMid}
        x2={CALLOUT_X}
        y2={bodyTop}
        stroke="var(--oxbo-p)"
      />
      <rect className="callout bit" x={CALLOUT_X} y={bodyTop - 22} width={CALLOUT_W} height={52} rx={8} />
      <text className="callout-name bit" x={CALLOUT_X + 16} y={bodyTop}>
        PDC bit
      </text>
      <text className="callout-spec" x={CALLOUT_X + 16} y={bodyTop + 18}>
        {odLabel} · 5 blade{vendor ? ` · ${vendor}` : ""}
      </text>
    </g>
  );
}
